/***********************************************************************************
 * This file makes use of == and != to reduce the number of comparisons being done *
 * https://jsbench.me/j9lb449aak                                                   *
 ***********************************************************************************/

/**
 * Check if parameter `x` is defined
 * @param x a nullable value
 * @returns `false` if it's null or undefined, `true` otherwise
 */
export function isDefined<T>(x: T | null | undefined): x is T {
  return x != null;
}

/**
 * Check if parameter `x` is not defined
 * @param x a nullable value
 * @returns `true` if it's null or undefined, `false` otherwise
 */
export function isNotDefined<T>(x: T | null | undefined): x is null | undefined {
  return x == null;
}

/**
 * Check if parameter `x` is defined and not empty
 * @param x a nullable string or array
 * @returns `false` if it's null or undefined or empty, `true` otherwise
 */
export function isDefinedAndNonEmpty<T extends string>(x: T | null | undefined): x is T;
export function isDefinedAndNonEmpty<T>(x: readonly T[] | null | undefined): x is readonly [T, ...T[]];
export function isDefinedAndNonEmpty<T>(x: T | null | undefined): x is T;
export function isDefinedAndNonEmpty<T extends { length?: number }>(x: T | string | null | undefined): x is T | string {
  // When x is not an array or a string, x.length === undefined so this will return true
  return x != null && x.length !== 0;
}

/**
 * Check if parameter `x` is defined and not empty
 * @param x a nullable string or array
 * @returns `false` if it's null or undefined or empty, `true` otherwise
 * @deprecated Use {@link isDefinedAndNonEmpty} instead
 */
export const isDefinedAndNotEmpty = isDefinedAndNonEmpty;

/**
 * Check if parameter `x` is either not defined or empty
 * @param x a nullable string or array
 * @returns `true` if it's null or undefined or empty, `false` otherwise
 */
export function isEmptyOrNotDefined(x: readonly unknown[] | null | undefined): x is [] | null | undefined;
export function isEmptyOrNotDefined(x: string | null | undefined): x is '' | null | undefined;
export function isEmptyOrNotDefined(x: readonly unknown[] | string | null | undefined): boolean {
  return x == null || x.length === 0;
}

/**
 * Check if parameter `x` is a string
 * @param x any value
 * @returns `true` if it's a string, `false` otherwise
 */
export function isString(x: unknown): x is string {
  return typeof x === 'string';
}

/**
 * Check if parameter `x` is empty
 * @param x a string or an array
 * @returns `true` if it's empty, `false` otherwise
 */
export function isEmpty<T>(x: readonly T[]): x is [];
export function isEmpty(x: string): x is '';
export function isEmpty<T>(x: readonly T[] | string): boolean {
  return x.length === 0;
}

/**
 * Check if parameter `x` is non empty
 * @param x a string or an array
 * @returns `false` if it's empty, `true` otherwise
 */
export function isNonEmpty<T>(x: readonly T[]): x is readonly [T, ...T[]];
export function isNonEmpty<T>(x: T[]): x is [T, ...T[]];
export function isNonEmpty<T extends string>(x: T | ''): x is T;
export function isNonEmpty<T>(x: readonly T[] | string): boolean {
  return x.length !== 0;
}

/**
 * Check if parameter object is empty
 * @param x an object (not an array)
 * @returns `true` if it's empty, `false` otherwise
 */
export function isEmptyObject<T extends object & { length?: never }>(x: Partial<T>): x is {} {
  // https://stackoverflow.com/a/59787784/1654241 solution A
  for (const _key in x) {
    return false;
  }
  return true;
}

/**
 * Check if parameter `x` is non empty
 * @param x an object
 * @returns `false` if it's empty, `true` otherwise
 */
export function isNonEmptyObject<T extends object & { length?: never }>(x: Partial<T>): x is T {
  // https://stackoverflow.com/a/59787784/1654241 solution A
  for (const _key in x) {
    return true;
  }
  return false;
}

/**
 * Higher Order function which return a values predicate
 * @param values Array of all the possible values that the predicate should match
 * @returns A type predicate for the values. If `values` is a tuple, the predicate will make Typescript infer this type when true.
 *
 * Calling this function is O(n) and consumes a bit of memory for the lookup table
 */
export function makeValuesPredicate<T extends string>(values: readonly T[]) {
  const lookupTable = values.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {} as Record<string, true | undefined>);
  /**
   * Values predicate
   * @param value a nullable string value
   * @returns `true` if `value` is among the values passed to create this predicate, `false` otherwise
   *
   * Calling this function is O(1)
   */
  return function isInValues(value: string | null | undefined): value is T {
    return (value != null && lookupTable[value]) ?? false;
  };
}

/**
 * Higher Order function which return a litteral union type predicate
 * @param allEnumValues Const array of all the union type litteral values
 * @returns A type predicate for the litteral union type
 * @deprecated Use {@link makeValuesPredicate} directly
 */
export const makeUnionLitteralPredicate = makeValuesPredicate;

export function not<T>(predicate: () => boolean): () => boolean;
export function not<T>(predicate: (arg: T) => boolean): (arg: T) => boolean;
export function not<L extends R, R>(predicate: (value?: R) => value is L): (value?: R) => value is Exclude<R, L>;
export function not<T>(predicate: (arg?: T) => boolean): (arg?: T) => boolean {
  return function inversePredicate(arg?: T) {
    return !predicate(arg);
  };
}
